import * as XLSX from 'xlsx';

function fmtN(v) {
  return Number(v) || 0;
}

// Always extract LOCAL date parts — never use toISOString() which converts to UTC
// and causes a 1-day shift for IST (UTC+5:30) when the time component is midnight.
function localDateStr(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function parseDate(v) {
  if (!v) return null;
  // Date object from SheetJS cellDates:true — use local getters, not UTC
  if (v instanceof Date) return localDateStr(v);
  // Numeric serial from SheetJS (no timezone involved)
  if (typeof v === 'number') {
    const d = XLSX.SSF.parse_date_code(v);
    if (d) return `${d.y}-${String(d.m).padStart(2,'0')}-${String(d.d).padStart(2,'0')}`;
  }
  // String like "2026-03-08 00:00:00" — slice the date part directly to avoid
  // timezone conversion that new Date() + toISOString() would introduce
  if (typeof v === 'string') {
    const iso = v.match(/^(\d{4}-\d{2}-\d{2})/);
    if (iso) return iso[1];
    const d = new Date(v);
    if (!isNaN(d)) return localDateStr(d);
  }
  return null;
}

// Returns { hubs, transactions }
export function parseXLSX(buffer) {
  const wb = XLSX.read(buffer, { type: 'array', cellDates: true });
  const ws = wb.Sheets[wb.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json(ws, { defval: null });

  const hubMap = {};
  const transactions = [];

  rows.forEach(r => {
    const lineStatus = r['Line Status'] || '-';
    const isClosed   = ['CLOSED', 'Closed', 'closed'].includes(lineStatus);

    const hub = r['Final Hub Name'] || r['Hub Name'] || 'Unknown';
    const zone = r['Zone'] || '-';
    const hubType = r['Hub Type'] || null;
    const toCollect = fmtN(r['Amount - To be collected']);
    const collected = fmtN(r['Collected']);
    const diff = fmtN(r['Diff']);
    const status = r['Status'] || '-';
    const month = r['Month'] || '-';
    const collectionDate = parseDate(r['Collection Date']);
    const srNo = r['Sr.No.'] ? String(r['Sr.No.']) : null;
    const txnId = r['Transaction Id'] ? String(r['Transaction Id']) : null;
    const partnerName = r['Partner Name'] || null;

    // Reconciliation columns — parse Credits first so deposited can use them
    const codCredit1 = fmtN(r['(+)COD Credit 1'] ?? r['COD Credit 1'] ?? 0);
    const codCredit2 = fmtN(r['(+)COD Credit 2'] ?? r['COD Credit 2'] ?? 0);

    // Total deposited = Credit 1 + Credit 2 (column P + Q), not column O
    const deposited = codCredit1 + codCredit2;
    const dnApplied  = r['(+)DN Applied'] != null ? fmtN(r['(+)DN Applied']) : null;
    const dnRef      = r['DN Ref'] ? String(r['DN Ref']) : null;
    const dnDate     = parseDate(r['DN Date']);
    const bankRef1   = r['Bank & date -1'] != null ? String(r['Bank & date -1']) : null;
    const ref1       = r['Ref-1'] != null ? String(r['Ref-1']) : null;

    if (!hubMap[hub]) {
      hubMap[hub] = {
        hub, zone, hub_type: hubType,
        totalToCollect: 0, totalCollected: 0,
        totalDeposited: 0, totalDiff: 0,
        openToCollect: 0, openDeposited: 0, openDiff: 0,
        openCount: 0, closedCount: 0,
        months: new Set(),
      };
    }
    hubMap[hub].totalToCollect += toCollect;
    hubMap[hub].totalCollected += collected;
    hubMap[hub].totalDeposited += deposited;
    hubMap[hub].totalDiff += diff;
    if (!isClosed) {
      hubMap[hub].openToCollect += toCollect;
      hubMap[hub].openDeposited += deposited;
      hubMap[hub].openDiff      += diff;
    }
    if (['Open', 'OPEN'].includes(lineStatus)) hubMap[hub].openCount += 1;
    if (isClosed) hubMap[hub].closedCount += 1;
    hubMap[hub].months.add(month);

    transactions.push({
      hub, sr_no: srNo, collection_date: collectionDate,
      month, zone, hub_type: hubType,
      amount_to_collect: toCollect, collected, deposited, diff,
      status, line_status: lineStatus,
      transaction_id: txnId, partner_name: partnerName,
      cod_credit_1: codCredit1, cod_credit_2: codCredit2,
      dn_applied: dnApplied, dn_ref: dnRef, dn_date: dnDate,
      bank_ref_1: bankRef1, ref_1: ref1,
    });
  });

  const hubs = Object.values(hubMap).map(h => ({ ...h, months: [...h.months].join(', ') }));
  return { hubs, transactions };
}

// Submission kind helper — reads the [SHORT] / [ZERO][...] prefix from remarks
function parseSubKind(remarks) {
  if (!remarks) return { kind: 'Full', category: '–', clean: '' };
  // Strip optional [ADMIN] prefix added by admin-submitted entries, then trim
  const core = remarks.trim().replace(/^\[ADMIN\]\s*/i, '').trim();
  if (core.startsWith('[LOSS]'))   return { kind: 'Loss/Utilized', category: '–', clean: core.replace(/^\[LOSS\]\s*/, '') };
  if (core.startsWith('[SHORT]'))  return { kind: 'Short',         category: '–', clean: core.replace(/^\[SHORT\]\s*/, '') };
  if (core.startsWith('[EXCESS]')) return { kind: 'Excess',        category: '–', clean: core.replace(/^\[EXCESS\]\s*/, '') };
  const zeroMatch = core.match(/^\[ZERO\]\[(.*?)\]\s*(.*)/s);
  if (zeroMatch) return { kind: 'Zero', category: zeroMatch[1], clean: zeroMatch[2] };
  return { kind: 'Full', category: '–', clean: core };
}

// Export submission history to Excel — used in Admin → Submission History download
export function exportSubmissionsToExcel(rows, filename) {
  const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const data = rows.map(s => {
    const { kind, category, clean } = parseSubKind(s.remarks);
    const modeLabel = { cms: 'CMS', neft_flipkart: 'NEFT→FK', neft_wheeley: 'NEFT→WH', zero: '–' };
    const [y, m] = (s.submission_date || '').split('-');
    const month = m ? `${MONTH_NAMES[parseInt(m, 10) - 1]}-${String(y).slice(2)}` : '–';
    return {
      'Hub':              s.hub || '–',
      'Month':            month,
      'Against Date':     s.txn_date || '–',
      'Amount Due (₹)':   s.txn_diff != null ? Number(s.txn_diff) : (s.is_direct ? 'Untracked Day' : '–'),
      'Submission Date':  s.submission_date || '–',
      'Submission Kind':  kind,
      'Zero Category':    category,
      'Payment Mode':     kind === 'Loss/Utilized' ? 'LOSS/UTILIZED' : (modeLabel[s.type] || s.type?.toUpperCase() || '–'),
      'Amount (₹)':       Number(s.amount) || 0,
      'Ref / UTR':        s.ref_number || '–',
      'Remarks':          clean || '–',
      'Submitted By':     s.submitted_by || '–',
      'Proof URL':        s.slip_url || '–',
      'Submitted At':     s.created_at ? new Date(s.created_at).toLocaleString('en-IN') : '–',
    };
  });
  const ws = XLSX.utils.json_to_sheet(data);
  ws['!cols'] = [
    { wch: 22 }, { wch: 10 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 22 },
    { wch: 14 }, { wch: 12 }, { wch: 18 }, { wch: 30 }, { wch: 18 }, { wch: 50 }, { wch: 22 },
  ];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Submissions');
  XLSX.writeFile(wb, filename || `Wheeley_Submissions_${new Date().toISOString().split('T')[0]}.xlsx`);
}

// ─── HUB USERS BULK IMPORT ────────────────────────────────────────────────────

// Download a blank template the admin fills in, then re-uploads
export function downloadUsersTemplate() {
  const sample = [
    { username: 'hub_delhi',   password: 'Password123', hub_name: 'DELHI HUB' },
    { username: 'hub_mumbai',  password: 'Password456', hub_name: 'MUMBAI HUB' },
  ];
  const ws = XLSX.utils.json_to_sheet(sample);
  ws['!cols'] = [{ wch: 20 }, { wch: 20 }, { wch: 30 }];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Hub Users');
  XLSX.writeFile(wb, 'Wheeley_Users_Import_Template.xlsx');
}

// Parse a users import Excel and return array of { username, password, hub_name }
// Throws if required columns are missing or no valid rows found.
export function parseUsersExcel(buffer) {
  const wb = XLSX.read(buffer, { type: 'array' });
  const ws = wb.Sheets[wb.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json(ws, { defval: '' });

  if (rows.length === 0) throw new Error('Excel file is empty.');

  const parsed = [];
  const errors = [];

  rows.forEach((row, idx) => {
    // Header keys are case-insensitive — normalize them
    const norm = {};
    Object.entries(row).forEach(([k, v]) => { norm[k.trim().toLowerCase()] = String(v).trim(); });

    const username = norm['username'] || norm['user name'] || norm['user_name'] || '';
    const password = norm['password'] || norm['pass'] || '';
    const hub_name = norm['hub_name'] || norm['hub name'] || norm['hub'] || '';

    if (!username) { errors.push(`Row ${idx + 2}: missing username`); return; }
    if (!password) { errors.push(`Row ${idx + 2}: missing password`); return; }
    if (!hub_name) { errors.push(`Row ${idx + 2}: missing hub_name`); return; }

    parsed.push({ username, password, hub_name });
  });

  return { users: parsed, errors };
}

// Export hub-wise date-wise deposit breakdown
// rows: array of { date, hub, amount_to_collect,
//   cms, cms_proofs[], neft_fkl, neft_fkl_proofs[],
//   neft_wh, neft_wh_proofs[], hub_utilised, hub_utilised_proofs[] }
export function exportHubSummaryToExcel(rows, filename) {
  const join = arr => (arr || []).filter(Boolean).join('\n') || '–';
  const data = rows.map(r => ({
    'Date':                      r.date,
    'Hub Name':                  r.hub,
    'Total to Collect (₹)':     r.amount_to_collect || 0,
    'CMS Deposit (₹)':           r.cms          || 0,
    'CMS Ref / UTR':             join(r.cms_refs),
    'CMS Proof Links':           join(r.cms_proofs),
    'NEFT to FKL (₹)':           r.neft_fkl     || 0,
    'NEFT to FKL Ref / UTR':     join(r.neft_fkl_refs),
    'NEFT to FKL Proof Links':   join(r.neft_fkl_proofs),
    'NEFT to WH (₹)':            r.neft_wh      || 0,
    'NEFT to WH Ref / UTR':      join(r.neft_wh_refs),
    'NEFT to WH Proof Links':    join(r.neft_wh_proofs),
    'Hub Utilised (₹)':          r.hub_utilised || 0,
    'Hub Utilised Ref / UTR':    join(r.hub_utilised_refs),
    'Hub Utilised Proof Links':  join(r.hub_utilised_proofs),
    'Total Deposited (₹)':       (r.cms || 0) + (r.neft_fkl || 0) + (r.neft_wh || 0),
  }));
  const ws = XLSX.utils.json_to_sheet(data);
  ws['!cols'] = [
    { wch: 14 }, { wch: 34 }, { wch: 22 },
    { wch: 18 }, { wch: 28 }, { wch: 50 },
    { wch: 18 }, { wch: 28 }, { wch: 50 },
    { wch: 18 }, { wch: 28 }, { wch: 50 },
    { wch: 18 }, { wch: 28 }, { wch: 50 },
    { wch: 20 },
  ];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Hub Deposits');
  XLSX.writeFile(wb, filename || `WHEELEY_HubDeposits_${new Date().toISOString().split('T')[0]}.xlsx`);
}

// ─── PAYMENT SHEET (ODH tab) ──────────────────────────────────────────────────

function numOrNull(v) {
  if (v == null || v === '') return null;
  const n = Number(v);
  return isNaN(n) ? null : n;
}

function strOrNull(v) {
  if (v == null) return null;
  const s = String(v).trim();
  return s === '' ? null : s;
}

/**
 * Normalise a row's keys: collapse whitespace/newlines in each header key,
 * then trim. Values are left untouched. This compensates for Google Sheets
 * returning column headers with subtle differences (line-breaks from wrapped
 * cells, extra spaces, etc.) vs. the literal strings the parser expects.
 *
 * We ONLY normalise whitespace — we do NOT lowercase — so that two headers
 * that differ only in case (e.g. "Pmt to be done" vs "PMT to be Done") are
 * still kept distinct after normalisation.
 */
function normaliseRowKeys(r) {
  const out = {};
  for (const [k, v] of Object.entries(r)) {
    const nk = k.replace(/[\r\n]+/g, ' ').replace(/  +/g, ' ').trim();
    // If two headers collapse to the same string keep the first occurrence.
    if (!Object.prototype.hasOwnProperty.call(out, nk)) out[nk] = v;
  }
  return out;
}

/**
 * Shared row mapper — works with both XLSX rows (parsePaymentSheet)
 * and Google Sheets API rows (parsePaymentSheetRows).
 * r is a plain object keyed by column header string.
 */
function mapPaymentRow(r) {
  return {
    pmt_hold:           strOrNull(r['Pmt to be done']),
    currently_active:   strOrNull(r['Currently active'] ?? r['Currently Active']),
    wm_name:            strOrNull(r['WM Name']),
    casper_employee_id: strOrNull(r['Casper Employee ID']),
    hub_name:           strOrNull(r['Hub Name']),
    state:              strOrNull(r['State']),
    // Metrics (read-only)
    total_attempted:    numOrNull(r['Total Attempted']),
    gtnl_delivered:     numOrNull(r['GTNL Delivered']),
    u2s:                numOrNull(r['U2S']),
    shopsy_delivered:   numOrNull(r['Shopsy Delivered']),
    odh_delivered:      numOrNull(r['ODH delivered'] ?? r['ODH Delivered']),
    man_days:           numOrNull(r['Man Days']),
    conversion:         numOrNull(r['Conversion']),
    rate:               numOrNull(r['Rate']),
    shopsy_rate_card:   numOrNull(r['Shopsy Rate Card']),
    proposed_rate:      numOrNull(r['Proposed rate'] ?? r['Proposed Rate']),
    mandays_inc:        numOrNull(r['Mandays Inc']),
    amount:             numOrNull(r['Amount']),
    penalty:            numOrNull(r['Penalty']),
    // Ground team editable (import existing values from sheet)
    advance:            numOrNull(r['Advance']),
    cod_loss:           numOrNull(r['COD Loss']),
    shipment_loss:      numOrNull(r['Shipment Loss']),
    // "shipment loss2" (control-tower-only deduction) and PAN link STATUS were
    // added as new columns in the ODH tab on 2026-10-22.
    shipment_loss2:     numOrNull(r['shipment loss2'] ?? r['Shipment Loss2'] ?? r['Shipment Loss 2']),
    scooty_rent:        numOrNull(r['Scooty Rent']),
    // NOTE: real sheets have used both 'PMT DECISION' (current) and 'PMT to be
    // Done' (older) as the header text for this column — check both.
    pmt_decision:       strOrNull(r['PMT DECISION'] ?? r['PMT to be Done']),
    remarks:            strOrNull(r['Remarks']),
    // Computed / admin
    rider_welfare:      numOrNull(r['Rider Wellfare'] ?? r['Rider Welfare']),
    tds:                numOrNull(r['Tds'] ?? r['TDS']),
    final_amount:       numOrNull(r['Final Amount']),
    pmt_status:         strOrNull(r['PMt Status'] ?? r['PMT Status']),
    remarks1:           strOrNull(r['Remarks1']),
    sheet_reference:    strOrNull(r['Sheet reference'] ?? r['Sheet Reference']),
    last_time_rate:     numOrNull(r['Last time rate'] ?? r['Last Time Rate']),
    per_pkt_rate:       numOrNull(r['Per pkt rate'] ?? r['Per Pkt Rate']),
    pan_card_check:     strOrNull(r['Pan Card Check']),
    // Account details
    acct_holder:        strOrNull(r['Account holder name'] ?? r['Account Holder Name']),
    acct_number:        strOrNull(r['Account Number']),
    bank_name:          strOrNull(r['Bank name'] ?? r['Bank Name']),
    ifsc_code:          strOrNull(r['ifsc Code'] ?? r['IFSC Code']),
    pan_no:             strOrNull(r['Pan No.'] ?? r['Pan No'] ?? r['PAN No.']),
    // PAN-Aadhaar link status (e.g. "Valid and Operative", "Invalid format") —
    // read-only, sourced from the sheet's STATUS column so ground teams can see
    // whose PAN needs correcting/linking without needing sheet access.
    pan_status:         strOrNull(r['STATUS']),
    // Write-back support: row index in the source Google Sheet (null for Excel uploads)
    sheet_row_index:    numOrNull(r['_sheet_row']),
  };
}

/**
 * Parse the ODH tab from a payment sheet Excel buffer.
 * Returns an array of rider row objects ready for Supabase insert.
 */
export function parsePaymentSheet(buffer) {
  const wb = XLSX.read(buffer, { type: 'array', cellDates: false });
  const wsName = wb.SheetNames.find(n => n.trim().toUpperCase() === 'ODH');
  if (!wsName) throw new Error('ODH tab not found in the uploaded file. Please check the sheet name.');
  const ws = wb.Sheets[wsName];
  const rows = XLSX.utils.sheet_to_json(ws, { defval: null, raw: true });
  return rows.map(mapPaymentRow).filter(r => r.wm_name && r.hub_name);
}

/**
 * Parse rider rows returned by the fetch-payment-sheet Edge Function.
 * rows = array of objects keyed by Google Sheet column headers.
 */
export function parsePaymentSheetRows(rows) {
  // Normalise keys first — Google Sheets API can return headers with extra
  // whitespace or newlines (e.g. from wrapped cells), which breaks exact
  // string lookups like r['Pmt to be done'].
  return rows.map(normaliseRowKeys).map(mapPaymentRow).filter(r => r.wm_name && r.hub_name);
}

/**
 * Export filtered payment riders to Excel.
 * finalAmtFn: function(rider) → number — passed in so the formula stays DRY.
 */
export function exportPaymentRidersToExcel(riders, finalAmtFn, filename) {
  const data = riders.map(r => ({
    'State':             r.state        || '–',
    'Hub Name':          r.hub_name     || '–',
    'Rider Name':        r.wm_name      || '–',
    'Casper ID':         r.casper_employee_id || '–',
    'Active':            r.currently_active   || '–',
    'PMT Hold':          r.pmt_hold     || '–',
    'Total Attempted':   r.total_attempted ?? '',
    'ODH Delivered':     r.odh_delivered   ?? '',
    'Man Days':          r.man_days        ?? '',
    'Rate (₹)':          r.rate            ?? '',
    'Amount (₹)':        r.amount          ?? '',
    'Penalty (₹)':       r.penalty != null ? Math.round(r.penalty) : '',
    'Advance (₹)':       r.advance         ?? '',
    'COD Loss (₹)':      r.cod_loss        ?? '',
    'Shipment Loss (₹)': r.shipment_loss   ?? '',
    'Shipment Loss 2 (₹)': r.shipment_loss2 ?? '',
    'Scooty Rent (₹)':   r.scooty_rent     ?? '',
    'TDS (₹)':           r.tds != null ? Math.round(r.tds) : '',
    'Final Amount (₹)':  Math.round(finalAmtFn(r)),
    'PMT Decision':      r.pmt_decision || '–',
    'Remarks':           r.remarks      || '–',
    'PMT Status':        r.pmt_status   || '–',
    'PAN Status':        r.pan_status   || '–',
    'Acct Holder':       r.acct_holder  || '–',
    'Acct Number':       r.acct_number  || '–',
    'Bank Name':         r.bank_name    || '–',
    'IFSC Code':         r.ifsc_code    || '–',
    'PAN No':            r.pan_no       || '–',
  }));

  const ws = XLSX.utils.json_to_sheet(data);
  ws['!cols'] = [
    { wch: 18 }, { wch: 28 }, { wch: 26 }, { wch: 16 }, { wch: 10 }, { wch: 14 },
    { wch: 14 }, { wch: 14 }, { wch: 10 }, { wch: 10 }, { wch: 12 }, { wch: 12 },
    { wch: 12 }, { wch: 14 }, { wch: 12 }, { wch: 14 }, { wch: 12 }, { wch: 10 }, { wch: 14 },
    { wch: 14 }, { wch: 30 }, { wch: 14 }, { wch: 18 }, { wch: 24 }, { wch: 18 }, { wch: 18 },
    { wch: 14 }, { wch: 14 },
  ];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Payment Sheet');
  XLSX.writeFile(wb, filename || `Wheeley_Payment_${new Date().toISOString().split('T')[0]}.xlsx`);
}

export function exportAuditLogToExcel(entries, filename) {
  const data = entries.map(e => ({
    'Time':       e.done_at ? new Date(e.done_at).toLocaleString('en-IN') : '–',
    'Done By':    e.done_by    || '–',
    'Hub':        e.hub_name   || '–',
    'Rider':      e.rider_name || '–',
    'FHRID':      e.casper_employee_id || '–',
    'Action':     e.action     || '–',
    'Field':      e.field_name || '–',
    'Old Value':  e.old_value  ?? '–',
    'New Value':  e.new_value  ?? '–',
  }));
  const ws = XLSX.utils.json_to_sheet(data);
  ws['!cols'] = [{ wch: 22 }, { wch: 18 }, { wch: 24 }, { wch: 24 }, { wch: 16 }, { wch: 14 }, { wch: 18 }, { wch: 20 }, { wch: 20 }];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Audit Log');
  XLSX.writeFile(wb, filename || `Wheeley_AuditLog_${new Date().toISOString().split('T')[0]}.xlsx`);
}

export function exportToExcel(rows, filename) {
  const data = rows.map(h => ({
    'Hub': h.hub,
    'Zone': h.zone,
    'Hub Type': h.hub_type || '-',
    'Total Diff (₹)': h.total_diff,
    'Submitted (₹)': h.total_submitted,
    'Pendency (₹)': h.pendency,
    'No. of Submissions': h.sub_count,
    'Status': h.pendency === 0 ? 'Cleared' : h.sub_count === 0 ? 'No Submission' : 'Partial',
  }));
  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Pendency Report');
  XLSX.writeFile(wb, filename || `WHEELEY_Pendency_${new Date().toISOString().split('T')[0]}.xlsx`);
}
